import React, { useEffect, useState } from "react";
import Header from "@/components/Header";
import HelpFab from "@/components/HelpFab";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { LESSONS } from "@/lib/academyLessons";
import { GraduationCap, Copy, Check, CheckCircle2 } from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return iso; }
}

export default function MyAcademy() {
  const [funnel, setFunnel] = useState(null);
  const [trainees, setTrainees] = useState([]);
  const [totalLessons, setTotalLessons] = useState(12);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [f, t] = await Promise.all([
          api.get("/academy/my/funnel"),
          api.get("/academy/my/trainees"),
        ]);
        setFunnel(f.data);
        setTrainees(t.data.trainees);
        setTotalLessons(t.data.total_lessons);
      } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
    })();
  }, []);

  const funnelUrl = funnel?.funnel_url?.startsWith("http")
    ? funnel.funnel_url
    : `${window.location.origin}${funnel?.funnel_url || ""}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(funnelUrl);
      setCopied(true); setTimeout(() => setCopied(false), 1400);
      toast.success("Funnel link copied — send it to your prospects");
    } catch { /* blocked */ }
  };

  return (
    <div className="min-h-screen pb-16">
      <Header />
      <div className="max-w-[1100px] mx-auto px-6 md:px-10 pt-10">
        <div className="mb-8 fade-up">
          <div className="pill mb-3"><span className="dot" />Your training funnel</div>
          <h1 className="font-display text-4xl md:text-5xl font-semibold tracking-tight">My Academy.</h1>
          <p className="text-ink-2 mt-2 text-sm">Send your personal funnel link — anyone who enrolls trains through CP Academy and shows up below, credited to you.</p>
        </div>

        {error && <div className="chip chip-amber mb-5" data-testid="myacademy-error">{error}</div>}

        {/* Funnel link card */}
        <div className="glass-strong p-6 md:p-7 mb-8 fade-up" data-testid="my-funnel-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "rgba(124,58,237,0.12)" }}>
              <GraduationCap size={18} style={{ color: "#7C3AED" }} />
            </div>
            <div>
              <div className="text-xs text-ink-3">Your personal funnel link</div>
              <h3 className="font-display text-lg font-semibold tracking-tight">Everything they need to get started — before they ever touch CaryPact.</h3>
            </div>
          </div>
          <div className="flex flex-col md:flex-row md:items-center gap-2.5">
            <code className="font-mono text-xs px-3 py-2.5 rounded-lg flex-1 overflow-x-auto whitespace-nowrap" style={{ background: "rgba(255,255,255,0.55)", border: "1px solid rgba(15,18,38,0.08)" }} data-testid="my-funnel-url">{funnelUrl}</code>
            <button onClick={copy} className="btn-primary" data-testid="my-funnel-copy">
              {copied ? <><Check size={13} /> Copied</> : <><Copy size={13} /> Copy link</>}
            </button>
          </div>
          <div className="text-[11px] text-ink-3 mt-2.5">
            The link carries your name (<span className="font-mono font-semibold">{funnel?.slug || "…"}</span>) so every enrollment is credited to you in the admin view. CaryPact invite links are assigned automatically at the “Join CaryPact” lesson.
          </div>
        </div>

        {/* My trainees */}
        <section className="glass-strong p-6 fade-up" data-testid="my-trainees-section">
          <h2 className="font-display text-lg font-bold text-ink mb-3">My trainees ({trainees.length})</h2>
          {trainees.length === 0 ? (
            <div className="text-sm text-ink-3 font-mono py-5 text-center" data-testid="my-trainees-empty">
              No trainees yet — copy your funnel link above and share it.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] font-mono uppercase tracking-wider text-ink-3">
                    <th className="py-2 pr-4">Trainee</th>
                    <th className="py-2 pr-4">Progress</th>
                    <th className="py-2 pr-4">Current lesson</th>
                    <th className="py-2 pr-4">Enrolled</th>
                    <th className="py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {trainees.map((t) => {
                    const prog = (t.academy_progress || []).length;
                    const pct = Math.round((prog / totalLessons) * 100);
                    const current = prog >= totalLessons ? "Graduated 🎓" : LESSONS[prog]?.title || "—";
                    return (
                      <tr key={t.id} className="border-t" style={{ borderColor: "rgba(15,18,38,0.06)" }} data-testid={`my-trainee-row-${t.id}`}>
                        <td className="py-3 pr-4">
                          <div className="font-semibold text-ink">{t.name}</div>
                          <div className="text-[12px] font-mono text-ink-3">{t.email}</div>
                        </td>
                        <td className="py-3 pr-4 min-w-[130px]">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(15,18,38,0.07)" }}>
                              <div className="h-full rounded-full" style={{ width: `${pct}%`, background: pct === 100 ? "#00863A" : "linear-gradient(90deg,#4060FF,#7C3AED)" }} />
                            </div>
                            <span className="text-[11px] font-mono text-ink-2 shrink-0">{prog}/{totalLessons}</span>
                          </div>
                        </td>
                        <td className="py-3 pr-4 text-[13px] text-ink-2 max-w-[220px] truncate">{current}</td>
                        <td className="py-3 pr-4 text-[12px] font-mono text-ink-3">{fmtDate(t.academy_enrolled_at)}</td>
                        <td className="py-3">
                          {t.academy_provisioned_at ? (
                            <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full" style={{ background: "rgba(64,96,255,0.1)", color: "#4060FF" }}>
                              <CheckCircle2 size={12} /> Member
                            </span>
                          ) : prog >= totalLessons ? (
                            <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full" style={{ background: "rgba(0,134,58,0.1)", color: "#00863A" }}>Graduated</span>
                          ) : (
                            <span className="text-[11px] font-mono text-ink-3">In training</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
      <HelpFab />
    </div>
  );
}
